import { WHATSAPP_NUMBER } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

// Enlaces oficiales (uno por canal). Airbnb cambia según la propiedad.
const INSTAGRAM_URL = "https://www.instagram.com/villaspuravida.tuxpan/";
const FACEBOOK_URL = "https://www.facebook.com/villaspuravidatuxpan";
const AIRBNB_HOST_URL =
  "https://www.airbnb.mx/users/profile/1468263920229106945";

/**
 * SocialIcons — componente reutilizable para WhatsApp, Instagram, Facebook y Airbnb.
 *
 * Variantes:
 *  - "topbar"  : barra oscura y delgada para colocar hasta arriba del sitio (dentro del Header).
 *  - "section" : sección completa con iconos grandes y copy. Ideal para "en medio" del Home
 *                o en una página de propiedad.
 *  - "inline"  : fila compacta de iconos (sin fondo), útil en sidebars o CTAs.
 *
 * Props:
 *  - variant: "topbar" | "section" | "inline"
 *  - airbnbUrl: URL de Airbnb específica (si no, usa el perfil del host)
 *  - whatsappMessage: mensaje pre-cargado para WhatsApp (opcional)
 *  - testPrefix: prefijo para data-testid (único por lugar de uso)
 */
export default function SocialIcons({
  variant = "section",
  airbnbUrl,
  whatsappMessage,
  testPrefix = "social",
}) {
  const { t, lang } = useLang();

  const waHref = whatsappMessage
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`
    : `https://wa.me/${WHATSAPP_NUMBER}`;

  const airbnbHref = airbnbUrl || AIRBNB_HOST_URL;

  const channels = [
    {
      key: "whatsapp",
      href: waHref,
      icon: "fa-brands fa-whatsapp",
      label: "WhatsApp",
      hint: lang === "es" ? "Chatea con nosotros" : "Chat with us",
      brand: "#25D366",
    },
    {
      key: "instagram",
      href: INSTAGRAM_URL,
      icon: "fa-brands fa-instagram",
      label: "Instagram",
      hint: lang === "es" ? "Ve nuestras historias" : "See our stories",
      brand: "#E4405F",
    },
    {
      key: "facebook",
      href: FACEBOOK_URL,
      icon: "fa-brands fa-facebook-f",
      label: "Facebook",
      hint: lang === "es" ? "Síguenos en Facebook" : "Follow us on Facebook",
      brand: "#1877F2",
    },
    {
      key: "airbnb",
      href: airbnbHref,
      icon: "fa-brands fa-airbnb",
      label: "Airbnb",
      hint: lang === "es" ? "Reseñas verificadas" : "Verified reviews",
      brand: "#FF5A5F",
    },
  ];

  // ─────────────────────── TOPBAR ───────────────────────
  if (variant === "topbar") {
    return (
      <div
        data-testid={`${testPrefix}-topbar`}
        className="bg-[#1F1A17] text-stone-200 border-b border-stone-800"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-9 flex items-center justify-between gap-3">
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`${testPrefix}-topbar-wa-phone`}
            className="hidden sm:inline-flex items-center gap-2 text-xs tracking-wide hover:text-[#E07A5F] transition-colors"
          >
            <i className="fa-brands fa-whatsapp text-sm text-[#25D366]" />
            +52 1 771 129 0073
          </a>
          <div className="flex items-center gap-1 ml-auto">
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.22em] text-stone-400 mr-3">
              {lang === "es" ? "Síguenos" : "Follow us"}
            </span>
            {channels.map((c) => (
              <a
                key={c.key}
                href={c.href}
                target="_blank"
                rel="noreferrer"
                aria-label={c.label}
                data-testid={`${testPrefix}-topbar-${c.key}`}
                className="w-7 h-7 inline-flex items-center justify-center text-stone-300 hover:text-white hover:bg-stone-800 transition-colors"
                style={{ "--hover": c.brand }}
                onMouseEnter={(e) => (e.currentTarget.style.color = c.brand)}
                onMouseLeave={(e) => (e.currentTarget.style.color = "")}
              >
                <i className={`${c.icon} text-sm`} />
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────── INLINE ───────────────────────
  if (variant === "inline") {
    return (
      <div
        data-testid={`${testPrefix}-inline`}
        className="flex items-center gap-2"
      >
        {channels.map((c) => (
          <a
            key={c.key}
            href={c.href}
            target="_blank"
            rel="noreferrer"
            aria-label={c.label}
            data-testid={`${testPrefix}-inline-${c.key}`}
            className="w-10 h-10 inline-flex items-center justify-center border border-stone-300 text-stone-700 hover:text-white transition-colors"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = c.brand;
              e.currentTarget.style.borderColor = c.brand;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "";
              e.currentTarget.style.borderColor = "";
            }}
          >
            <i className={`${c.icon} text-base`} />
          </a>
        ))}
      </div>
    );
  }

  // ─────────────────────── SECTION (default) ───────────────────────
  return (
    <section
      data-testid={`${testPrefix}-section`}
      className="bg-stone-50 py-16 md:py-24 border-y border-stone-200"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <p className="text-eyebrow text-[#E07A5F] mb-4">
          {lang === "es" ? "Conéctate con nosotros" : "Connect with us"}
        </p>
        <h3 className="font-serif-display text-3xl md:text-4xl text-stone-900 leading-tight mb-4">
          {lang === "es"
            ? "Reserva directo, pregunta por WhatsApp o síguenos."
            : "Book directly, ask on WhatsApp or follow us."}
        </h3>
        <p className="text-stone-700 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
          {lang === "es"
            ? "Respondemos en minutos. Mira la vida de la playa en Instagram y Facebook, y revisa nuestras reseñas verificadas en Airbnb."
            : "We reply in minutes. See the beach life on Instagram and Facebook, and check our verified reviews on Airbnb."}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {channels.map((c) => (
            <a
              key={c.key}
              href={c.href}
              target="_blank"
              rel="noreferrer"
              aria-label={c.label}
              data-testid={`${testPrefix}-section-${c.key}`}
              className="group bg-white border border-stone-200 p-6 flex flex-col items-center gap-3 hover:-translate-y-1 hover:shadow-xl transition-all"
            >
              <span
                className="w-14 h-14 inline-flex items-center justify-center rounded-full text-white transition-transform group-hover:scale-110"
                style={{ backgroundColor: c.brand }}
              >
                <i className={`${c.icon} text-xl`} />
              </span>
              <span className="font-semibold text-stone-900 text-sm md:text-base">
                {c.label}
              </span>
              <span className="text-xs text-stone-500 leading-tight">{c.hint}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
